import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Hide on scroll down (past a small threshold), reveal on scroll up.
      if (y > lastY && y > 140) setHidden(true);
      else if (y < lastY) setHidden(false);
      lastY = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      hidden && !isMobileMenuOpen ? '-translate-y-full' : 'translate-y-0'
    } ${
      isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
    }`}>
      <nav className="container mx-auto px-8 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => scrollToSection('hero')}
            className="hover:opacity-70 transition-opacity"
          >
            <img 
              src="/soulboard-logo.png" 
              alt="Soulboard" 
              className="h-8 w-auto"
              style={{ 
                mixBlendMode: 'multiply',
                filter: 'contrast(1.1)'
              }}
            />
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-5">
            <button data-testid="nav-about" onClick={() => scrollToSection('meet-melinda')} className="nav-link text-sm font-medium uppercase tracking-wide">{t('about')}</button>
            <button data-testid="nav-method" onClick={() => scrollToSection('soulboard-method')} className="nav-link text-sm font-medium uppercase tracking-wide">{t('whatIsSoulboard')}</button>
            <button data-testid="nav-services" onClick={() => scrollToSection('services')} className="nav-link text-sm font-medium uppercase tracking-wide">{t('offerings')}</button>
            <button data-testid="nav-faq" onClick={() => scrollToSection('faq')} className="nav-link text-sm font-medium uppercase tracking-wide">{t('faqTitle')}</button>
            <button data-testid="nav-contact" onClick={() => scrollToSection('contact')} className="nav-link text-sm font-medium uppercase tracking-wide">{t('contact')}</button>
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 border-2 border-neutral-300 text-neutral-900 text-xs uppercase tracking-wide font-semibold hover:border-neutral-900 transition-colors"
            >
              {language === 'en' ? '🇭🇺 HU' : '🇬🇧 EN'}
            </button>

            <button 
              onClick={() => scrollToSection('contact')}
              className="px-5 py-2 text-neutral-900 text-xs uppercase tracking-wide font-bold transition-all hover:opacity-90"
              style={{ background: '#d4ff00' }}
            >
              {t('bookNow')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg py-4 animate-fade-in">
            <div className="flex flex-col gap-4 px-6">
              <button data-testid="nav-mobile-about" onClick={() => scrollToSection('meet-melinda')} className="nav-link text-left">{t('about')}</button>
              <button data-testid="nav-mobile-method" onClick={() => scrollToSection('soulboard-method')} className="nav-link text-left">{t('whatIsSoulboard')}</button>
              <button data-testid="nav-mobile-services" onClick={() => scrollToSection('services')} className="nav-link text-left">{t('offerings')}</button>
              <button data-testid="nav-mobile-faq" onClick={() => scrollToSection('faq')} className="nav-link text-left">{t('faqTitle')}</button>
              <button data-testid="nav-mobile-contact" onClick={() => scrollToSection('contact')} className="nav-link text-left">{t('contact')}</button>
              <button
                onClick={toggleLanguage}
                className="px-4 py-2 border-2 border-neutral-300 text-neutral-900 text-xs uppercase tracking-wide font-semibold text-center"
              >
                {language === 'en' ? '🇭🇺 HU' : '🇬🇧 EN'}
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="px-6 py-2 text-neutral-900 text-sm uppercase tracking-wide text-center font-bold"
                style={{ background: '#d4ff00' }}
              >
                {t('bookNow')}
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
